import { memo, useMemo } from 'react';

import { useIntl } from 'react-intl';

import {
  Capabilities,
  ChildPipelineStep,
  Pipeline,
  Repository,
  Step,
} from 'src/components/pipelines/models';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from 'src/features';

import {
  ALLOWANCE_LIMIT,
  FIX_FAILED_STEP_TRIGGER,
  PARENT_STEP_TRIGGER,
  PIPELINES_FINAL_STEP_ENABLED,
} from '../../constants';
import { getFeatureFlagFromStep } from '../../utils';
import { Action, DisabledState } from '../Action';
import MultiActionButton from '../MultiActionButton';
import SingleActionButton from '../SingleActionButton';
import { PipelineActionsWrapper } from '../styled';

import { messages } from './PipelineStatus.i18n';

export enum PipelineActionType {
  RerunSteps = 'RERUN_STEPS',
  RerunPipeline = 'RERUN_PIPELINE',
  Stop = 'STOP',
  TransitionToStop = 'TRANSITION_TO_STOP',
}

export interface Props {
  capabilities: Capabilities;
  repository: Repository;
  pipeline: Pipeline;
  steps: (Step | ChildPipelineStep)[];
  actionInProgress: boolean;
  onClick: (pipeline: Pipeline, actionKey: PipelineActionType) => void;
}

const PipelineActions = ({
  capabilities,
  repository,
  pipeline,
  steps,
  actionInProgress,
  onClick,
}: Props) => {
  const { formatMessage } = useIntl();

  const isPipelinePausedHaltedToStopEnabled = useStatsigGate(
    StatsigFeatureKeys.isPipelinePausedHaltedToStopEnabled
  );

  const nonChildSteps = useMemo(
    () => steps.filter(step => !step.isChildStep),
    [steps]
  ) as Step[];
  const hasUnfinishedRedeploy = useMemo(
    () =>
      nonChildSteps.some(step =>
        step.stage
          ? step.stage.is_redeploy && step.stage.isUnfinished
          : step.isRedeployed && step.isRerunnable
      ),
    [nonChildSteps]
  );

  const stepWithExpiredDeployment = useMemo(
    () =>
      nonChildSteps.filter(
        step =>
          !!step.stage &&
          step.stage.is_deployment_stage &&
          step.stage.expired &&
          !step.stage.is_redeploy &&
          step.stage.isUnfinished
      )[0],
    [nonChildSteps]
  );

  const hasBreachedAcceptableUsePolicy = useMemo(
    () =>
      steps.some(
        step =>
          step.statusReason != null &&
          step.statusReason.isAcceptableUserPolicyBreachedReason
      ),
    [steps]
  );

  const disabled: { [key: string]: DisabledState } = useMemo(
    () => ({
      missingRepoWritePermissions: new DisabledState(
        !repository.canWrite,
        'You do not have write permissions.'
      ),
      fetchingCapabilities: new DisabledState(
        !capabilities.hasFetchedCapabilities,
        ''
      ),
      pipelinesDisabled: new DisabledState(
        !capabilities.pipelinesEnabled && capabilities.hasFetchedCapabilities,
        'Pipelines is disabled. Enable Pipelines to rerun the build.'
      ),
      noBuildMinutes: new DisabledState(
        capabilities.limited &&
          capabilities.allowancePercentageUsed >= ALLOWANCE_LIMIT,
        'Your account has run out of build minutes.'
      ),
      customPipelineWithVariables: new DisabledState(
        pipeline.has_variables,
        "This pipeline ran with temporary variable values so we can't rerun."
      ),
      hasUnfinishedRedeploy: new DisabledState(
        hasUnfinishedRedeploy,
        'To rerun failed steps all redeployments must be complete and successful.'
      ),
      hasExpiredStages: new DisabledState(
        !!stepWithExpiredDeployment,
        `Another pipeline has been deployed to the ${
          stepWithExpiredDeployment &&
          stepWithExpiredDeployment?.environmentName
        } environment.`
      ),
      hasBreachedAcceptableUsePolicy: new DisabledState(
        hasBreachedAcceptableUsePolicy,
        "You've breached Atlassian's Acceptable Use Policy."
      ),
      isChildPipeline: new DisabledState(
        pipeline.triggerType === PARENT_STEP_TRIGGER,
        'Pipeline operations are only available on parent pipelines'
      ),
      isFixFailedStepPipeline: new DisabledState(
        pipeline.triggerType === FIX_FAILED_STEP_TRIGGER,
        'Pipelines triggered by the "fix failed step" trigger cannot be rerun.'
      ),
      isActionRequired: new DisabledState(
        pipeline.isActionRequired,
        'This pipeline was halted because a child pipeline needs input to resume. Resume the child pipeline to continue.'
      ),
    }),
    [
      capabilities,
      repository.canWrite,
      pipeline.has_variables,
      hasUnfinishedRedeploy,
      stepWithExpiredDeployment,
      hasBreachedAcceptableUsePolicy,
      pipeline.triggerType,
      pipeline.isActionRequired,
    ]
  );

  const allActionsDisabled = useMemo(
    () =>
      disabled.missingRepoWritePermissions
        .or(disabled.fetchingCapabilities)
        .or(disabled.pipelinesDisabled)
        .or(disabled.noBuildMinutes)
        .or(disabled.isChildPipeline)
        .or(disabled.isFixFailedStepPipeline)
        .or(disabled.hasBreachedAcceptableUsePolicy)
        .or(disabled.isActionRequired),
    [disabled]
  );
  const canRerunFailedSteps = useMemo(
    () => pipeline.canRerunSteps && steps.length > 1,
    [pipeline.canRerunSteps, steps.length]
  );
  const isStopHaltedPausedPipelineActionAllowed = useMemo(() => {
    return (
      isPipelinePausedHaltedToStopEnabled &&
      !pipeline.isRunning &&
      (pipeline.status === 'PAUSED' || pipeline.status === 'HALTED')
    );
  }, [
    isPipelinePausedHaltedToStopEnabled,
    pipeline.status,
    pipeline.isRunning,
  ]);

  const runningStep = useMemo(() => {
    return nonChildSteps.find(step => step.isSyncing);
  }, [nonChildSteps]);

  const shouldUseStopFinalStep = useMemo(() => {
    return (
      runningStep?.isFinal &&
      getFeatureFlagFromStep(
        runningStep?.featureFlags,
        PIPELINES_FINAL_STEP_ENABLED
      )
    );
  }, [runningStep]);

  const actions: { [key in PipelineActionType]: Action } = useMemo(
    () => ({
      [PipelineActionType.RerunSteps]: {
        key: PipelineActionType.RerunSteps,
        name: pipeline.isStopped
          ? formatMessage(messages.rerunStoppedSteps)
          : formatMessage(messages.rerunFailedSteps),
        isVisible: canRerunFailedSteps,
        disabled: disabled.hasUnfinishedRedeploy.or(disabled.hasExpiredStages),
        onClick: () => onClick(pipeline, PipelineActionType.RerunSteps),
      },
      [PipelineActionType.RerunPipeline]: {
        key: PipelineActionType.RerunPipeline,
        name: !canRerunFailedSteps
          ? formatMessage(messages.rerun)
          : formatMessage(messages.rerunEntirePipeline),
        isVisible: !pipeline.isRunning && !pipeline.isQueued,
        disabled: disabled.customPipelineWithVariables,
        onClick: () => onClick(pipeline, PipelineActionType.RerunPipeline),
      },
      [PipelineActionType.Stop]: {
        key: PipelineActionType.Stop,
        name: shouldUseStopFinalStep
          ? formatMessage(messages.stopFinalStepButton)
          : formatMessage(messages.stopButton),
        isVisible: pipeline.isRunning || pipeline.isQueued,
        disabled: disabled.missingRepoWritePermissions
          .or(disabled.fetchingCapabilities)
          .or(disabled.pipelinesDisabled),
        onClick: () => onClick(pipeline, PipelineActionType.Stop),
      },
      [PipelineActionType.TransitionToStop]: {
        key: PipelineActionType.TransitionToStop,
        name: formatMessage(messages.closePipeline),
        isVisible: isStopHaltedPausedPipelineActionAllowed,
        disabled: disabled.missingRepoWritePermissions
          .or(disabled.fetchingCapabilities)
          .or(disabled.pipelinesDisabled),
        onClick: () => onClick(pipeline, PipelineActionType.TransitionToStop),
      },
    }),
    [
      pipeline,
      disabled,
      canRerunFailedSteps,
      onClick,
      isStopHaltedPausedPipelineActionAllowed,
      formatMessage,
      shouldUseStopFinalStep,
    ]
  );

  if (steps.length === 0) {
    return <></>;
  }
  const actionsArr = isStopHaltedPausedPipelineActionAllowed
    ? [actions.RERUN_STEPS, actions.RERUN_PIPELINE, actions.TRANSITION_TO_STOP]
    : [actions.RERUN_STEPS, actions.RERUN_PIPELINE];

  return (
    <>
      <PipelineActionsWrapper pipeline={pipeline}>
        {actions.RERUN_STEPS.isVisible || actions.RERUN_PIPELINE.isVisible ? (
          <MultiActionButton
            title="Rerun"
            isLoading={actionInProgress}
            disabled={allActionsDisabled}
            actions={actionsArr}
          />
        ) : actions.STOP.isVisible ? (
          <SingleActionButton
            isLoading={actionInProgress}
            action={actions.STOP}
          />
        ) : null}
      </PipelineActionsWrapper>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(PipelineActions);
